import { create } from '@thoughtspot/logger';

const logger = create('pending-client-state-mutations');

// Upper bound on how long save waits, measured from when save starts. A gated
// mutation that is dropped before it runs (useGatedBachMutation
// overwrite/unmount) never settles, and an unbounded wait would hang save.
export const PENDING_CLIENT_STATE_WAIT_TIMEOUT_MS = 10_000;

// A never-settling mutation is forgotten after this long even if nobody saves,
// so the module-level map doesn't hold it for the life of the tab. Much longer
// than the save wait so a slow but live mutation is still waited for.
export const PENDING_CLIENT_STATE_CLEANUP_MS = 60_000;

// Client-state mutations still waiting on the server, per answer session.
// They write the cache optimistically, but a non-optimistic cache read (as
// save does) only sees the new value once the response lands.
const pendingMutations = new Map<string, Set<Promise<void>>>();

export const trackPendingClientStateMutation = (
    sessionId: string,
    mutation: Promise<unknown>,
): void => {
    if (!sessionId || !mutation) return;
    const pending = pendingMutations.get(sessionId) ?? new Set();
    pendingMutations.set(sessionId, pending);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settled: Promise<void> = Promise.race([
        Promise.resolve(mutation).then(
            (): void => undefined,
            (): void => undefined,
        ),
        new Promise<void>(resolve => {
            timer = setTimeout(resolve, PENDING_CLIENT_STATE_CLEANUP_MS);
        }),
    ]).then((): void => {
        clearTimeout(timer);
        pending.delete(settled);
        if (pending.size === 0 && pendingMutations.get(sessionId) === pending) {
            pendingMutations.delete(sessionId);
        }
        return undefined;
    });
    pending.add(settled);
};

// Resolves once every client-state mutation started for the session so far has
// settled, or after PENDING_CLIENT_STATE_WAIT_TIMEOUT_MS. Never rejects: a
// failed mutation leaves the cache as the server has it.
export const waitForPendingClientStateMutations = async (
    sessionId: string,
): Promise<void> => {
    const pending = pendingMutations.get(sessionId);
    if (!pending?.size) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = await Promise.race([
        Promise.all(Array.from(pending)).then(() => false),
        new Promise<boolean>(resolve => {
            timer = setTimeout(
                () => resolve(true),
                PENDING_CLIENT_STATE_WAIT_TIMEOUT_MS,
            );
        }),
    ]);
    clearTimeout(timer);
    if (timedOut) {
        logger.warn(
            `Client-state mutations for session ${sessionId} did not settle within ${PENDING_CLIENT_STATE_WAIT_TIMEOUT_MS}ms; saving without them.`,
        );
    }
};
